import { accountService } from '@/api/services/account-service'
import type { Account, LoginRequest, LoginResponse, SignupRequest } from '@/types/account.types'
import type { ApiResponse } from '@/types/api-response.types'
import type { UploadFileResponse } from '@/types/upload-file.types'
import axios from 'axios'
import { defineStore } from 'pinia'
import { computed, reactive } from 'vue'
interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  account: Account | null
}

export const useAuthStore = defineStore('auth', () => {
  const state = reactive<AuthState>({
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
    account: localStorage.getItem('account') ? JSON.parse(localStorage.getItem('account') as string) : null
  })

  const isAuthenticated = computed(() => state.refreshToken !== null && state.account !== null && state.account.loggedIn === true)

  async function login(request: LoginRequest): Promise<SignupRequest> {
    try {
      const response: LoginResponse = await accountService.login(request)
      setTokens(response)
      await getUserInfo()

      return {
        socialKey: request.socialKey,
        subSocialKey: request.subSocialKey,
        socialType: request.socialType,
        subscribedAds: false,
        redirect: 'dashboard',
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiResponse<null>
        switch (errorResponse.result.code) {
          case 40000001:
            return {
              socialKey: request.socialKey,
              subSocialKey: request.subSocialKey,
              socialType: request.socialType,
              subscribedAds: false,
              redirect: 'signup',
            }
          case 40100004:
            throw new Error('정지된 계정입니다.')
          case 40100005:
            throw new Error('탈퇴한 계정입니다.')
          default:
            throw error
        }
      }
      throw error
    }
  }

  async function signup(request: SignupRequest) {
    try {
      const response: LoginResponse = await accountService.signup(request)
      setTokens(response)
      await getUserInfo()
    } catch (error) {
      throw error
    }
  }

  async function getAccessToken() {
    const response: LoginResponse = await accountService.getAccessToken(state.refreshToken as string)
    setTokens(response)
  }

  async function getUserInfo() {
    const response: Account = await accountService.getMyAccount()
    state.account = response
    localStorage.setItem('account', JSON.stringify(state.account))
  }

  async function logout() {
    try {
      const response: void = await accountService.logout(state.refreshToken as string)
    } catch (error) {
      console.error(error)
    } finally {
      clearTokens()
    }
  }

  async function withdrawal() {
    try {
      const response: void = await accountService.withdrawal()
    } catch (error) {
      console.error(error)
    } finally {
      clearTokens()
    }
  }

  async function updateSubscribedAds(subscribedAds: boolean) {
    const response = await accountService.updateConsent({
      subscribedAds: subscribedAds,
    })

    if (state.account) {
      state.account.subscribedAds = response.subscribedAds
      localStorage.setItem('account', JSON.stringify(state.account))
    }
  }

  const clearTokens = () => {
    state.accessToken = null
    state.refreshToken = null
    state.account = null
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('account')
  }

  async function uploadProfileImage(image: File | null): Promise<void> {
    if (image === null) {
      await updateProfileImage(null, null)
      return
    }

    const response: UploadFileResponse = await accountService.uploadProfileImage(image)
    const profileIdx = response.idx
    const profileUrl = response.url

    await updateProfileImage(profileIdx, profileUrl)
  }

  async function updateProfileImage(profileIdx: number | null, profileUrl: string | null) {
    const response: void = await accountService.updateProfileImage(profileIdx)
    if (state.account) {
      state.account.profileIdx = profileIdx
      state.account.profileUrl = profileUrl
      localStorage.setItem('account', JSON.stringify(state.account))
    }
  }

  function setTokens(response: LoginResponse) {
    state.accessToken = response.accessToken
    state.refreshToken = response.refreshToken
    localStorage.setItem('accessToken', state.accessToken ?? null)
    localStorage.setItem('refreshToken', state.refreshToken ?? null)
  }

  return {
    state,
    isAuthenticated,
    login,
    signup,
    getAccessToken,
    getUserInfo,
    clearTokens,
    logout,
    withdrawal,
    updateSubscribedAds,
    uploadProfileImage,
  }
})
